/**
 * The live game, over the realtime plane — phase 5f.
 *
 * Separate from `gameService.ts` (which is the lobby) because they are two
 * different conversations: the lobby is a handful of taps and one topic, and
 * this is three topics, two of which carry the same payload type for different
 * audiences.
 *
 * # Two streams, and the page needs both
 *
 * | Topic | Carries |
 * |---|---|
 * | `board_topic` | `snapshot(game, Display)` — every side, the phase, the round |
 * | `player_topic` | your own slice: your grids, your submission, your shop |
 * | `clip_topic` | a cue to start playing, which is an instruction and not state |
 * | `Lobby.topic` | who is here, the mode, the teams — everything before the game |
 *
 * **The lobby rides on this connection too**, rather than on a second one. The
 * party page needs the roster before the game and the board during it, and two
 * sockets would mean two one-shot tickets, two auth handshakes per page load,
 * and a page holding two connections that can disagree about which party it is
 * in. Phase 10 folded the old `gameService.ts` in here and deleted it.
 *
 * They arrive independently and in no guaranteed order, so this **merges** them
 * rather than making the page reconcile: the board is taken whole and `you` is
 * taken from whichever message carried one. A board message never carries `you`
 * — by construction, server-side — so it must not be allowed to erase it.
 *
 * # Topic names come from the server, and matching is by hash
 *
 * `watch` answers with the names. A push frame carries only the topic's
 * **hash** — never its name — so every push arrives on one `"push"` event and a
 * subscriber matches with `SonolumeClient.isPushTopic`, which hashes the string
 * it subscribed to.
 *
 * `client.on("game:abc", …)` is therefore a handler that **never fires**: `on`
 * is a plain event emitter keyed by event name, and the only event a push is
 * emitted under is `"push"`. It looks exactly like a working subscription and is
 * silent forever. Found by playing a real round end to end; nothing else would
 * have caught it.
 */

import {
  SonolumeClient,
  SpheromakApiError,
  type ClipCue,
  type GameRules,
  type GameState,
  type Lobby,
  type PushEvent,
  type SideLabel,
  type Snapshot,
  type Watching,
  type YouView,
} from "../generated/client.js";
import { socketTicket } from "../partyService.js";
import type { GameHandlers, GameLink } from "./types.js";

/** The strategy name `src/rpc/auth.rs` registers. A wire value — keep in step. */
const DEVICE_STRATEGY = "sonolume-device";

/**
 * Whether the server refused this because the session is gone.
 *
 * Two shapes on purpose. Since spheromak 1.2.0 a refusal arrives as a
 * structured `SpheromakApiError` carrying `AUTH_REQUIRED`, and matching the
 * **code** is the durable test. Before that it was a bare `Error` whose only
 * distinguishing feature was English prose, and a television running an older
 * packaged build (ADR 0005) is exactly the client that would still send one — so
 * the string is kept as a fallback rather than as the mechanism.
 *
 * ⚠️ The two messages differ by three words. `"Authentication required"` is the
 * **server** refusing; `"Authentication required for this method"` is the
 * client refusing to send at all. The prefix match covers both, which is what we
 * want here — either way this device cannot act.
 */
function isSessionGone(cause: unknown): boolean {
  if (cause instanceof SpheromakApiError) {
    return cause.code === "AUTH_REQUIRED";
  }
  return cause instanceof Error && cause.message.startsWith("Authentication required");
}

/**
 * How often to tell the server this device is still here.
 *
 * Comfortably under `[party] drop_after_seconds` (180 by default), so it takes
 * **four** consecutive failures to lose a seat rather than one slow request. The
 * two numbers are related and live in different repositories, which is a real
 * hazard: raise the server's window and this stays correct, lower it below ~135s
 * and a playing phone starts getting dropped.
 */
const HEARTBEAT_MS = 45_000;

/**
 * Connect, subscribe, and start receiving state.
 *
 * The ticket is fetched here rather than taken as an argument: it is one-shot
 * and sixty seconds old at most, so a caller holding one would be holding
 * something that has probably already expired. `socketTicket` reads the
 * `HttpOnly` device cookie server-side, which is the only way a *browser* can
 * get a socket credential at all.
 *
 * `seatToken` is the exception, and it exists for exactly one caller: a packaged
 * television (phase 11c) has no cookie jar the server can read, so it holds its
 * device token itself and presents it here. Everything after the ticket is
 * identical — same strategy, same topics, same board.
 */
export async function connectToGame(
  url: string,
  handlers: GameHandlers,
  seatToken?: string,
): Promise<GameLink> {
  const seat = await socketTicket(seatToken);

  const client = new SonolumeClient(url);
  await client.connect();
  await client.authenticateWithToken(DEVICE_STRATEGY, seat.ticket);

  // The merge target. Held here rather than in React state because two topics
  // write to it and the page must never see half an update.
  let board: Snapshot | null = null;
  let you: YouView | null = null;
  let live = false;
  let rules: GameRules | null = null;
  let layout = "couch";
  let roster: SideLabel[] = [];
  // Held past the end of the game on purpose: `watch` reads it from the *live*
  // row, so a phone that asked again after the final settlement would get
  // nothing — and the moment it needs the id is exactly then.
  let gameId: string | null = null;
  let lobby: Lobby | null = null;
  let phaseEndsAt: number | null = null;
  let phaseSeconds: number | null = null;

  const publish = () => {
    if (rules) {
      handlers.onState({
        live,
        board,
        you,
        rules,
        deviceLayout: layout,
        roster,
        gameId,
        lobby,
        phaseEndsAt,
        phaseSeconds,
      });
    }
  };

  /** Everything `watch` says about the *party* rather than about the game. */
  const takeConfig = (watching: Watching) => {
    rules = watching.rules;
    layout = watching.device_layout;
    roster = watching.roster;
    gameId = watching.game_id ?? gameId;
  };

  // Registered **before** `watch`, so the republish it triggers is not dropped.
  // The alternative — subscribe, then attach — loses whatever arrives in
  // between, and what arrives in between is the entire current state.
  const onGameState = (data: unknown, isPrivate: boolean) => {
    const message = data as GameState;
    const started = message.live && !live;
    live = message.live;
    if (started) {
      // **The party's configuration is read once, and the party keeps changing
      // until it starts.** Somebody who joined after this socket connected is
      // missing from `roster`, and a layout the host flipped in the lobby is
      // stale — which decides whether this device plays audio at all.
      //
      // The moment the game goes live is exactly the moment both stop moving,
      // and `watch` is already idempotent and already the reconnect path. The
      // republish it triggers arrives with `live` already true, so this cannot
      // recurse.
      void client.game
        .watch(undefined)
        .then((fresh) => {
          takeConfig(fresh);
          publish();
        })
        .catch(() => {
          // The names stay as they were, which is a board with a stale label on
          // it rather than no board. Not worth interrupting a round for.
        });
    }
    // **Anchored the moment it arrives**, because that is when "in 24 seconds"
    // is true. Both topics carry the same clock — a deadline is not private —
    // so whichever message lands last re-anchors it, which costs a few
    // milliseconds of accuracy and keeps one rule for both.
    const clock = message.phase_clock ?? null;
    phaseEndsAt = clock ? Date.now() + clock.ends_in_ms : null;
    phaseSeconds = clock?.total_seconds ?? null;

    if (isPrivate) {
      // A private message carries a snapshot rendered for *this* player. Its
      // board half is identical to the shared one, so taking `you` and leaving
      // the board alone keeps one source for each half.
      you = message.state?.you ?? null;
    } else {
      board = message.state ?? null;
    }
    publish();
  };

  // ⚠️ **The handler goes on before the first `watch`, and the queue is why it
  // can.** `game.watch` subscribes this connection *and republishes the current
  // snapshot* when a game is live (`src/rpc/game/mod.rs`), so the board arrives
  // in the gap between the call and the reply — and a handler attached after the
  // reply misses it. That is invisible before a game starts, because there is
  // nothing to republish, and it is exactly what made **a reload mid-game land
  // in the lobby**: `live` came back true from the reply, the board push was
  // dropped, and `PartyPage` renders the lobby whenever the board is null.
  //
  // A push carries only a topic *hash*, so an event that arrives before the
  // reply cannot be matched to a name we do not have yet. Hence a queue rather
  // than a reorder: hold everything until the names are in hand, then drain it
  // through the same matcher in arrival order.
  let topics: Watching | null = null;
  let lobbyTopic: string | null = null;
  const early: PushEvent[] = [];

  const route = (event: PushEvent) => {
    if (!topics) {
      early.push(event);
      return;
    }
    if (SonolumeClient.isPushTopic(event, topics.board_topic)) {
      onGameState(event.decode(), false);
      return;
    }
    if (topics.player_topic && SonolumeClient.isPushTopic(event, topics.player_topic)) {
      onGameState(event.decode(), true);
      return;
    }
    if (handlers.onClip && SonolumeClient.isPushTopic(event, topics.clip_topic)) {
      handlers.onClip(event.decode() as ClipCue);
      return;
    }
    // The party topic carries exactly one payload type — `rpc::party::publish`
    // is the only thing that writes to it — so the narrowing is structural
    // rather than a guess about what arrived.
    if (lobbyTopic && SonolumeClient.isPushTopic(event, lobbyTopic)) {
      lobby = event.decode() as Lobby;
      publish();
    }
  };

  // One handler for every topic, matched by hash. See the module header: a
  // per-topic `on(name, …)` compiles, reads correctly, and never fires.
  client.on("push", (raw) => {
    route(raw as PushEvent);
  });

  const watching: Watching = await client.game.watch(undefined);
  takeConfig(watching);

  // **The lobby, on the same connection.** `party.watch` subscribes and answers
  // with the current state, and every later change arrives on the topic it
  // names — so the roster, the mode and the teams come from one source rather
  // than from a reply reconciled against a broadcast.
  //
  // The topic string comes from the server (`Lobby.topic`) rather than being
  // rebuilt here. A push carries only the topic's hash, so a client guessing
  // the format stops matching silently the day the server changes it; the old
  // `gameService.ts` guessed, with a comment saying it mirrored the Rust.
  const firstLobby: Lobby = await client.party.watch(undefined);
  lobby = firstLobby;
  lobbyTopic = firstLobby.topic;

  // Both replies are in, so the names are known: open the gate and let through
  // whatever arrived while it was shut, in the order it arrived.
  topics = watching;
  live = watching.live;
  const queued = early.splice(0, early.length);
  for (const event of queued) {
    route(event);
  }

  publish();

  // ---------------------------------------------------------------------------
  // Coming back from a dropped socket
  // ---------------------------------------------------------------------------
  //
  // ⚠️ **The client cannot re-authenticate itself here, and it will try.** On a
  // new socket it attempts a session resume and, failing that, re-sends *the
  // credentials it stored* — which for sonolume is the socket ticket: one-shot,
  // sixty seconds, and already spent the first time. So the transport comes
  // back, the subscriptions come back, and `isAuthenticated` stays false. The
  // first thing the player presses then throws `Authentication required for this
  // method` from the generated client, and only a reload fixes it, because a
  // reload mints a new ticket.
  //
  // Seen in a real party (phase 14 A1), and it fires on **every server
  // restart** — which now includes every deploy.
  //
  // So: mint a fresh ticket and authenticate again. Then re-`watch` regardless
  // of whether the resume worked, because `watch` is idempotent, republishes,
  // and is the only thing that catches this client up on the rounds it missed
  // while it was away.
  const recover = async () => {
    if (!client.isAuthenticated) {
      const fresh = await socketTicket(seatToken);
      await client.authenticateWithToken(DEVICE_STRATEGY, fresh.ticket);
    }
    const again: Watching = await client.game.watch(undefined);
    takeConfig(again);
    topics = again;
    live = again.live;
    const relobby: Lobby = await client.party.watch(undefined);
    lobby = relobby;
    lobbyTopic = relobby.topic;
    publish();
  };

  // ---------------------------------------------------------------------------
  // "I am still here"
  // ---------------------------------------------------------------------------
  //
  // **Presence is measured in calls, and a real game has long stretches with
  // none.** In Classic a player who is not the active side cannot guess or place
  // at all — the engine refuses them — so at four players their turn comes round
  // every eight minutes or so. A `/display` is the extreme case: it renders and
  // sends nothing, ever, by design (ADR 0005). Both would be dropped for
  // behaving exactly as intended.
  //
  // So both send this, from the one place both of them already share. Any other
  // call counts too — the server marks presence on every resolved caller — which
  // is why this is a fallback rather than the mechanism, and why a player who is
  // playing never actually needs one.
  //
  // ⚠️ **`setInterval`, not the socket's ping.** The transport already pings and
  // a browser answers those from a tab nobody has looked at in an hour, which is
  // precisely what must NOT read as present. A timer in page script stops when a
  // phone locks or a tab suspends — which is the distinction the drop window is
  // trying to draw.
  const heartbeat = setInterval(() => {
    void client.party.here(undefined).catch((cause: unknown) => {
      // A missed beat is not an error — the window is several beats wide for
      // exactly this reason, and the socket's own recovery owns the rest.
      //
      // A *refused* beat is different, and this is the cheapest place in the
      // system to notice it: the heartbeat is the one call that happens whether
      // or not anybody is touching the screen, so a session that lapsed while a
      // phone sat on a table is discovered here rather than under the next tap.
      if (isSessionGone(cause)) {
        clearInterval(heartbeat);
        handlers.onHealth?.("expired");
      }
    });
  }, HEARTBEAT_MS);

  client.on("reconnecting", () => {
    handlers.onHealth?.("reconnecting");
  });
  client.on("reconnected", () => {
    void recover().then(
      () => {
        handlers.onHealth?.("live");
      },
      () => {
        // A ticket the server refuses means this browser's seat is gone — it
        // left the party, or the party did. Rebuilding the link is the page's
        // job, and it knows how to offer joining again.
        handlers.onHealth?.("lost");
      },
    );
  });
  client.on("reconnectFailed", () => {
    handlers.onHealth?.("lost");
  });

  return {
    client,
    partyId: seat.party_id,
    profileId: seat.profile_id,
    backToLobby: async () => {
      // The same pair `recover` uses, for the same reason: `watch` is
      // idempotent, republishes, and is the only thing that catches a client up.
      const again: Watching = await client.game.watch(undefined);
      takeConfig(again);
      topics = again;
      live = again.live;
      board = null;
      const relobby: Lobby = await client.party.watch(undefined);
      lobby = relobby;
      lobbyTopic = relobby.topic;
      publish();
    },
    disconnect: () => {
      clearInterval(heartbeat);
      client.disconnect();
    },
  };
}
